import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { createInvitationSchema, type CreateInvitationInput } from "@handoverpro/shared";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";

// The role the invitee is joined at on redeem (design §4). Mirrors the roster's
// membership-role options; defaults to a plain member.
const ROLE_OPTIONS: { value: CreateInvitationInput["role"]; label: string }[] = [
  { value: "member", label: "Member" },
  { value: "admin", label: "Admin" },
];

export interface InviteMemberDialogProps {
  open: boolean;
  onSubmit: (input: CreateInvitationInput) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  /** A server-side error (e.g. a 403) surfaced above the actions. */
  serverError?: string | null;
}

/**
 * Invite-by-email modal (V2-E3.3, HAN-88). Where {@link AddMemberDialog} adds an
 * already-registered colleague, this invites an email that may not have an
 * account yet: it records a pending invitation (E3.2) and emails an accept link.
 * The invitee completes their own GMC-capturing signup and is auto-joined to the
 * ward at the chosen role on redeem-at-registration. Validates with the same
 * shared `createInvitationSchema` the API enforces.
 */
export function InviteMemberDialog({
  open,
  onSubmit,
  onOpenChange,
  serverError = null,
}: InviteMemberDialogProps): JSX.Element {
  const form = useForm<CreateInvitationInput>({
    resolver: zodResolver(createInvitationSchema),
    defaultValues: { email: "", role: "member" },
    mode: "onBlur",
  });

  // Reset whenever the dialog (re)opens so a previous attempt never lingers.
  useEffect(() => {
    if (open) form.reset({ email: "", role: "member" });
    // form.reset is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isSubmitting = form.formState.isSubmitting;

  const handleSubmit = form.handleSubmit((values) => onSubmit(values));

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite by email</DialogTitle>
          <DialogDescription>
            Send an invite link to a colleague who doesn’t have an account yet. They’ll join this
            ward automatically once they register.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form noValidate onSubmit={handleSubmit} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="off"
                      placeholder="alice@example.nhs.uk"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ROLE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Admins can manage this ward’s team and patients; members can’t.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {serverError ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Send invite
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
